// Libraries

import { DocumentIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { signAsset } from '@/api/assets';
// Assets
import { getDocumentTypes, patchDocument } from '@/api/Sherpa/Draw';
import type { PatchDocumentPayload } from '@/api/Sherpa/Draw/types';
import { getTdmsByProjectPhase } from '@/api/tdm';
import Button from '@/components/UI/Button';
// custom components
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { SherpaProjectReactQueryKey, TdmReactQueryKey } from '@/constants/ReactQueryKeys';
import { useFileUpload } from '@/hooks/useFileUpload';
import { notifyUser } from '@/store/Toast';
import { byteConverter, convertEnumToText, formatDate3, getAcceptedFileTypes, sanitizeResponseError } from '@/utils';
import { Phases } from '../NewDocument/constant';
import { validationSchema } from '../NewDocument/validationSchema';

interface EditDocumentProps {
  handleModal: () => void;
  projectName: string;
  document: any;
}

export type TFile = {
  contentType: string;
  createdAt: string;
  key: string;
  name: string;
  size: number;
};

const EditBaseDocument = ({ handleModal, projectName, document }: EditDocumentProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { projectId, drawId, documentId } = useParams();
  const { watch, setValue } = useFormContext();

  const transformDataRef = useRef<any>(null);
  const {
    metaData: { documents },
  } = document?.approval?.[document?.approval.length - 1] || { metaData: { documents: [] } };

  const [recentFiles, setRecentFiles] = useState<TFile[]>(documents);

  const phase = watch('phase');

  //Tanstack: version 5 of useQuery implementation
  const fetchDeliverables = useQuery({
    queryKey: [TdmReactQueryKey.GET_PROJECT_DELIVERABLES, projectId, phase.value],
    queryFn: () =>
      getTdmsByProjectPhase({
        type: 'deliverables',
        phase: phase.value,
        page: 1,
        perPage: 10,
        projectId: Number(projectId),
      }),
  });

  const deliverables = fetchDeliverables?.data?.data.map((d: any) => {
    return { label: d.name, value: d.id };
  });

  //Tanstack: version 5 of useQuery implementation
  const fetchDocumentTypes = useQuery({
    queryKey: ['get-document-types'],
    queryFn: () => getDocumentTypes(),
  });
  const documentTypes = fetchDocumentTypes?.data?.data;

  const { upload } = useFileUpload({ signAsset: signAsset });

  const editDocument = useMutation(
    (data: PatchDocumentPayload) =>
      patchDocument({
        data,
        projectId: Number(projectId),
        drawId: Number(drawId),
        documentId: Number(documentId),
      }),
    {
      onSuccess: (_res) => {
        //Tanstack: version 5 query implementation
        queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_DOCUMENT] });
        queryClient.invalidateQueries({ queryKey: ['ProjectDrawDocument-audit'] });

        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Document updated successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const uploadFile = useMutation(
    (payload: {
      signAssetPayload: {
        key: string;
        type: string;
        resource: string;
      }[];
      files: File[];
    }) => upload(payload),
    {
      onSuccess: (response) => {
        const assetsFiles = response?.files.map((file: any, index: number) => {
          return {
            name: file.name,
            key: response?.sign.fKey ?? response.sign[index].fKey,
            contentType: file.type,
            size: file.size,
          };
        });

        editDocument.mutate({
          ...transformDataRef.current,
          documents: [...assetsFiles, ...(transformDataRef?.current?.documents || [])],
        });
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const submitEditDocumentHandler = (data: any) => {
    const { documents } = data;

    const transformedRecentFiles = recentFiles.map(({ contentType, key, name, size }) => ({
      contentType,
      key,
      name,
      size,
    }));

    transformDataRef.current = {
      phaseSlug: data.phase?.value,
      documentTypeSlug: data?.documentType?.value,
      name: data.name,
      summary: data.summary,
      documents: transformedRecentFiles,
    };

    if (data?.deliverableId?.id) {
      transformDataRef.current.deliverableId = Number(data.deliverableId.value);
    }

    if (documents?.length) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      documents?.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(',');
        if (acceptedFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
            toastType: 'failed',
          })
        );
      }

      if (validFiles.length > 0) {
        const signPayload = validFiles.map<{
          key: string;
          type: string;
          resource: string;
        }>((file) => ({
          key: file.name,
          type: file.type,
          resource: 'projectDrawDocument',
        }));

        uploadFile.mutate({
          signAssetPayload: signPayload,
          files: validFiles,
        });
      }
    } else {
      editDocument.mutate(transformDataRef.current);
    }
  };

  useEffect(() => {
    if (deliverables && document.deliverableId) {
      setValue(
        'deliverableId',
        deliverables.find((d: any) => d.value === document.deliverableId)
      );
    }
  }, [document.deliverableId, setValue, deliverables]);

  const handleRemoveFiles = (file: TFile) => {
    const updatedFiles = recentFiles.filter((f) => f.key !== file.key);
    setRecentFiles(updatedFiles);
  };

  return (
    <HookForm onSubmit={submitEditDocumentHandler} className='space-y-2 grid grid-cols-2 gap-4'>
      <h3 className='text-sm col-span-2'>Project Name: {projectName}</h3>
      <HookReactSingleSelect componentClassName='col-span-1' label='Phase' name='phase' options={Phases} placeholder='' required />
      <HookReactSingleSelect
        componentClassName='col-span-1'
        label='Document Type'
        name='documentType'
        options={documentTypes}
        placeholder=''
        required
      />
      <HookReactSingleSelect
        componentClassName='col-span-2'
        label='Deliverable'
        name='deliverableId'
        options={deliverables}
        placeholder=''
      />
      <div className='col-span-2'>
        <HookFileInput label='Document' name='documents' id='documents' multiple />
      </div>
      {recentFiles && recentFiles.length > 0 && (
        <div className='col-span-2 space-y-1'>
          <p>Recent Uploads</p>
          <ul>
            {recentFiles.map((file) => (
              <li key={file.createdAt} className='flex justify-between items-center border rounded-md p-2'>
                <div className='flex gap-2 items-center'>
                  <DocumentIcon className='w-7 h-7 flex-none' />
                  <div className='flex flex-col'>
                    <h5 className='text-sm capitalize'>{file.name}</h5>
                    <div className='flex text-xs gap-5 text-neutralColor-normal'>
                      <span>Date: {formatDate3(file.createdAt)}</span>
                      <span>Size: {byteConverter(file.size, 2)}</span>
                    </div>
                  </div>
                </div>
                <XCircleIcon className='w-4 h-4 hover:text-tertiaryColor-normal cursor-pointer' onClick={() => handleRemoveFiles(file)} />
              </li>
            ))}
          </ul>
        </div>
      )}
      <HookInput customClassName='col-span-2' name='name' label='Document Name (50 character max)' type='text' />
      <div className='col-span-2'>
        <HookTextarea name='summary' label='Document Summary (200 character max)' rows={2} />
      </div>
      <div className='flex gap-4 mt-5 justify-end col-span-2'>
        <Button
          label='Cancel'
          type='button'
          btnStyle='btn-neutral'
          disabled={editDocument.isLoading || uploadFile.isLoading}
          onClick={handleModal}
        />
        <Button
          label={`${editDocument.isLoading || uploadFile.isLoading ? 'Updating...' : 'Edit'}`}
          type='submit'
          btnStyle='btn-primary'
          disabled={editDocument.isLoading || uploadFile.isLoading}
        />
      </div>
    </HookForm>
  );
};

export const EditDocument = (props: EditDocumentProps) => {
  const { document } = props;

  const formattedData = {
    phase: Phases.find((p: any) => p.value === document.phaseSlug),
    documentType: {
      label: convertEnumToText(document?.documentTypeSlug),
      value: document?.documentTypeSlug,
    },
    name: document?.name,
    summary: document?.summary,
  };
  return (
    <Modal>
      <h2 className='mt-0'>Edit Document</h2>
      <HookFormProvider defaultValues={formattedData} resolver={yupResolver(validationSchema) as any}>
        <EditBaseDocument {...props} />
      </HookFormProvider>
    </Modal>
  );
};
